import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Badge } from "./ui/badge";
import { Globe } from "lucide-react";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));

// Dashboard insight card: monetary ESG data grouped by facility currency, converted to a
// single reporting currency via the FX layer. Defaults to the tenant's reporting currency.
export default function MultiLocaleFxCard({ year }) {
  const [d, setD] = useState(null);
  const [ccy, setCcy] = useState("");

  const load = useCallback(() => {
    api.get("/dashboard/fx-rollup", { params: { year, ...(ccy ? { currency: ccy } : {}) } })
      .then((r) => { setD(r.data); if (!ccy) setCcy(r.data.target_currency); })
      .catch(() => {});
  }, [year, ccy]);
  useEffect(() => { load(); }, [load]);

  if (!d) return null;
  const target = ccy || d.target_currency;

  return (
    <div className="card-flat p-5" data-testid="fx-rollup-card">
      <div className="flex items-start justify-between gap-3">
        <div className="inline-flex items-center gap-2">
          <div className="h-8 w-8 rounded-sm bg-accent/10 border border-accent/20 grid place-items-center"><Globe className="h-4 w-4 text-accent" /></div>
          <div>
            <div className="font-display font-semibold text-sm">Multi-locale spend</div>
            <div className="text-[11px] text-muted-foreground">Monetary ESG data · FY{d.year} · as of {d.as_of_date}</div>
          </div>
        </div>
        <Select value={target} onValueChange={setCcy}>
          <SelectTrigger className="w-24 h-8 text-xs" data-testid="fx-currency-select"><SelectValue /></SelectTrigger>
          <SelectContent>{(d.currency_options || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-3">
        <div className="font-mono text-2xl font-bold" data-testid="fx-grand-total">{d.target_currency} {fmt(d.grand_total)}</div>
        {d.has_pending && <Badge variant="warning" className="text-[9px] mt-1" data-testid="fx-pending">Some rates pending</Badge>}
      </div>

      {d.rows.length === 0 ? (
        <div className="mt-3 text-[11px] text-muted-foreground" data-testid="fx-empty">No monetary data captured for FY{d.year}.</div>
      ) : (
        <div className="mt-3 space-y-1.5">
          <div className="label-eyebrow text-[10px]">By source currency</div>
          {d.rows.map((r) => (
            <div key={r.currency} className="flex items-center justify-between text-[11px]" data-testid={`fx-row-${r.currency}`}>
              <span className="font-mono text-muted-foreground">{r.currency} {fmt(r.original_total)}</span>
              <span className="font-mono">{r.fx_status === "ready"
                ? `→ ${d.target_currency} ${fmt(r.converted_total)}`
                : <Badge variant="warning" className="text-[9px]">rate pending</Badge>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
